import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Import custom components
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import { VerySmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'

import DeleteModal from './report_list/ScenarioDeleteReport'

// Import context
import { useProjectContext } from '../../../context/ProjectContext'

// Import styled components
import {
  ActionButtonsContainer,
  StatsContainer,
} from './styles/ScenarioReportList.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'
import theme from '../../../styles/theme'
import { useClientContext } from '../../../context/ClientContext'

// Server-side pagination: the grid shows one page, the API owns the full list.
// PAGE_SIZE doubles as VersatileGrid `maxDisplayedItems`.
const PAGE_SIZE = 10

const ReportList = () => {
  const [reports, setReports] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { selectedClient } = useClientContext()
  const { selectedProject } = useProjectContext()

  const [refresh, setRefresh] = useState(new Date().getTime())
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedReportToDelete, setSelectedReportToDelete] = useState(null)

  const handleDeleteClick = (rowData) => {
    setSelectedReportToDelete(rowData)
    setDeleteModalOpen(true)
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false)
    setSelectedReportToDelete(null)
  }

  const handleViewReport = (reportId) => {
    // Navigate to report view page
    navigate(`/scenarios/${reportId}`, {
      state: {
        id: reportId,
        origin: '/insights/saved_scenarios',
      },
    })
  }

  // Fetch reports (one page; server owns filtering/sorting/pagination)
  const clientId = selectedClient?.id
  const projectId = selectedProject?.id

  // Global filter changes invalidate the current page
  useEffect(() => {
    setPage(1)
  }, [clientId, projectId])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchReports = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })
        if (clientId) {
          params.set('client_id', String(clientId))
        }
        if (projectId) {
          params.set('project_id', String(projectId))
        }
        if (search) {
          params.set('search', search)
        }
        if (sort) {
          params.set(
            'ordering',
            `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
          )
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/scenarios/reports/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages (e.g. last item on the page was
        // deleted) — step back to the previous page and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const result = await response.json()
        setReports(result.data || [])
        setTotal(result.total ?? result.data?.length ?? 0)
      } catch (error) {
        // Ignore abort errors - they're expected when we cancel stale requests
        if (error.name !== 'AbortError') {
          console.error(error)
          setReports([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }
    fetchReports()

    // Cleanup: abort the request if dependencies change before it completes
    return () => {
      abortController.abort()
    }
  }, [projectId, refresh, clientId, page, search, sort])

  // // Function to flatten reports data for search functionality
  // const getFlattenedReportsData = () => {
  //     return reports.map(report => {
  //         const fitScore = report.fit_score || 0;
  //         const qualityStatus = getFitnessStringByScore(fitScore);

  //         return {
  //             ...report,
  //             solution: report.project_solution?.solution?.name || '',
  //             project: report.project_solution?.project?.name || '',
  //             client: report.project_solution?.project?.client?.name || '',
  //             created_by: report.created_by?.name || report.created_by,
  //             // Add searchable quality status string
  //             fit_score_quality: qualityStatus,
  //             // Add variables count
  //             variables_count: report.independent_drivers?.length || 0
  //         };
  //     });
  // };

  // Grid configuration
  // NOTE(server-pagination): only `name`/`created_at` are sortable server-side
  // (`ordering_fields = ["id", "name", "created_at", "updated_at"]` and
  // `search_fields = ["name", "slug", "outcome_variable", "solution", "created_by__name"]` in
  // `citrus_api/scenarios/views.py`). Model/adjusted-drivers/fit-score/
  // creator columns are display only until the backend gains related-field
  // search/ordering.
  const headers = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
    },
    {
      title: 'Date',
      key: 'created_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      center: true,
      serverKey: 'created_at',
    },
    {
      title: 'Model name',
      key: 'model_name',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Model',
      key: 'model',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Adj. drivers',
      key: 'variables_count',
      type: 'number',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Fit score',
      key: 'fit_score_quality',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
      getSortValue: (row) => row.fit_score,
    },
    {
      title: 'Created by',
      key: 'created_by',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'View / Delete',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  // Grid header stats (server `total` — the grid only holds one page now)
  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>Saved scenarios: {total}</span>
    </StatsContainer>
  )

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(nextSort)
  }, [])

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Saved scenarios</SectionTitle>
        <ClientProjectPicker />
      </HeaderContainer>

      <SimpleAccordion id={'reportListPageAccordion'} defaultActiveKey={['1']}>
        <SimpleAccordion.Panel eventKey="1" noCollapse>
          <VersatileGrid
            gridContainerStyle={{
              gridTemplateColumns:
                '1.8fr 0.7fr 0.7fr 0.7fr 0.6fr 0.6fr 1fr 0.8fr',
            }}
            headers={headers}
            data={reports}
            renderRow={(rowData) => [
              <GridCellBoldText>{rowData.name}</GridCellBoldText>,
              <GridCellSmallFadedText>
                {new Date(rowData?.created_at).toISOString().slice(0, 10)}
              </GridCellSmallFadedText>,
              <GridCellNormalText>
                {rowData.artifact_name || 'No model name'}
              </GridCellNormalText>,
              <GridCellNormalText>
                <VerySmallNormalTag>{rowData?.solution}</VerySmallNormalTag>
              </GridCellNormalText>,
              <GridCellNormalText>
                {rowData.adjusted_drivers_count ?? 0}
              </GridCellNormalText>,
              <GridCellNormalText>
                {rowData?.model_fit_score}
              </GridCellNormalText>,
              <GridCellNormalText>
                {rowData?.created_by?.name || rowData.created_by}
              </GridCellNormalText>,
              <ActionButtonsContainer>
                <CustomIcons.faEye
                  color={theme.colors.fadedText}
                  size="xs"
                  onClick={() => handleViewReport(rowData?.id)}
                  style={{ cursor: 'pointer' }}
                  solid={false}
                  data-testid={`view-${rowData.name}`}
                />
                <CustomIcons.TrashIcon
                  color={theme.colors.fadedText}
                  size="xs"
                  onClick={() => handleDeleteClick(rowData)}
                  style={{ cursor: 'pointer' }}
                  data-testid={`delete-${rowData.name}`}
                />
              </ActionButtonsContainer>,
            ]}
            headerInfo={gridHeader}
            maxDisplayedItems={PAGE_SIZE}
            serverSide
            serverTotal={total}
            serverPage={page}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            loading={loading}
            searchPlaceholder="Find saved report..."
            loadingMessage={'Loading reports...'}
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>

      {selectedReportToDelete && (
        <DeleteModal
          report={selectedReportToDelete}
          setRefresh={setRefresh}
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
        />
      )}
    </>
  )
}

export default ReportList
